import { Component, ElementRef, ViewChild, AfterViewInit, Input } from '@angular/core';
import ForceGraph3D from '3d-force-graph';
import SpriteText from 'three-spritetext';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import * as THREE from 'three';
import { forceManyBody, forceLink } from 'd3-force-3d';

@Component({
  selector: 'app-whole-view',
  templateUrl: './whole-view.component.html',
  styleUrls: ['./whole-view.component.scss'],
})
export class WholeViewComponent implements AfterViewInit {
  @ViewChild('graphContainer', { static: true }) graphEl!: ElementRef<HTMLDivElement>;
  private graph: any;
  selectedProject: {
    id: string;
    description?: string;
    domain?: string;
    techMaturity?: string;
    mainConcept?: string;
    subConcept?: string;
    team?: string;
    projectStatus?: string;
    createdOn?: string;
    STICTeam?: string;
    stakeholders?: string;
    path?: string;
  } | null = null;
  selected = 'whole_graph';
  @Input() fullGraphData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };
  currentGraphData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };

  ngAfterViewInit() {
    (window as any).graph = this.graph;

    // TODO: move graph setup/initialization into service
    this.graph = new ForceGraph3D(this.graphEl.nativeElement)
      .width(this.graphEl.nativeElement.offsetWidth)
      .height(this.graphEl.nativeElement.offsetHeight)
      .nodeAutoColorBy('type')
      .nodeLabel((node: any) => (node.type === 'project' ? `${node.id || 'Untitled'}` : node.id))
      .backgroundColor('#000003') // 1F2A38
      .d3Force('charge', forceManyBody().strength(-400))
      .d3Force('link', forceLink().distance(250))
      .nodeThreeObject((node: any) => {
        const group = new THREE.Group();

        let color = 'red';
        if (node.type === 'mainConcept' || node.type === 'technology') {
          color = 'blue';
        } else if (node.type === 'subConcept') {
          color = 'white';
        }

        const radius = node.type === 'project' ? 10 : 20; // smaller for project nodes
        const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius), new THREE.MeshBasicMaterial({ color }));
        group.add(sphere);

        // Add rings if it's a subConcept
        if (node.type === 'subConcept') {
          const labels = ['adopt', 'trial', 'assess', 'identified', 'none'];
          const ringMaterial = new THREE.MeshBasicMaterial({ color: 'gray', side: THREE.DoubleSide });

          labels.forEach((label, index) => {
            const radius = 50 + index * 40;
            const ring = new THREE.Mesh(new THREE.RingGeometry(radius - 1, radius, 64), ringMaterial.clone());
            ring.rotation.z = Math.PI / 2;
            ring.position.y = -1.5 * index;
            group.add(ring);

            const ringLabel = new SpriteText(label);
            ringLabel.color = 'white';
            ringLabel.textHeight = 12;

            const angle = index * 0.5;
            const x = radius * Math.cos(angle);
            const y = radius * Math.sin(angle);
            const z = 0;

            ringLabel.position.set(x, y, z);
            group.add(ringLabel);
          });
        }

        // Add text label above sphere
        const label = new SpriteText(node.id);
        label.color = 'white';
        label.textHeight = 20;
        label.position.set(0, 30, 0); // Position above sphere, note sphere radisu
        group.add(label);

        return group;
      })
      .onNodeClick((node: any) => {
        if (node.type === 'project') {
          this.selectedProject = {
            id: node.id,
            domain: node.domains ? 'Domain: ' + node.domains : 'Domain: Unknown',
            techMaturity:
              node.techMaturity.length > 0 ? 'Tech Maturity: ' + node.techMaturity : 'Tech Maturity: Unknown',
            team: node.team.length > 0 ? 'Team: ' + node.team : 'Team: Unknown',
            STICTeam: node.STIC_team.length > 0 ? 'Team Members: ' + node.STIC_team : 'Team Members: Unknown',
            stakeholders: node.stakeholders.length > 0 ? 'Stakeholders: ' + node.stakeholders : 'Stakeholders: Unknown',
            mainConcept:
              node.mainConcepts.length > 0 ? 'Main Technologies: ' + node.mainConcepts : 'Main Technologies: Unknown',
            subConcept:
              node.subConcepts.length > 0 ? 'Sub Technologies: ' + node.subConcepts : 'Sub Technologies: Unknown',
            description: node.markdown ? 'Description: ' + node.markdown : 'No description available.',
            path: node.path ? node.path : 'Unknown Path',
          };
        } else if (node.type === 'mainConcept') {
          this.selectedProject = null;
          this.showMainConceptView(node.id);
        } else if (node.type === 'subConcept') {
          this.showSubConceptView(node.id);
        } else {
          this.selectedProject = null;
          this.expandNode(node.id);
        }
      })
      .onEngineTick(() => {
        // force 2d
        const gData = this.graph.graphData();
        gData.nodes.forEach((n: any) => {
          n.z = 0;
          n.vz = 0;
        });
      })
      .onNodeDrag((node: any) => {
        if (node.type !== 'project' || !node.parentSubConcept || !node.targetRadius) return;

        const centerNode = this.fullGraphData.nodes.find((n) => n.id === node.parentSubConcept);
        if (!centerNode) return;

        const dx = node.x - centerNode.x;
        const dy = node.y - centerNode.y;
        const angle = Math.atan2(dy, dx);
        const radius = node.targetRadius;

        node.x = centerNode.x + radius * Math.cos(angle);
        node.y = centerNode.y + radius * Math.sin(angle);
        node.z = 0;
      })
      .onNodeDragEnd((node: any) => {
        if (node.type !== 'project' || !node.parentSubConcept || !node.targetRadius) return;

        const centerNode = this.fullGraphData.nodes.find((n) => n.id === node.parentSubConcept);
        if (!centerNode) return;

        const dx = node.x - centerNode.x;
        const dy = node.y - centerNode.y;
        const angle = Math.atan2(dy, dx);
        const radius = node.targetRadius;

        // Snap into place
        node.x = centerNode.x + radius * Math.cos(angle);
        node.y = centerNode.y + radius * Math.sin(angle);
        node.z = 0;

        node.fx = node.x;
        node.fy = node.y;
        node.fz = 0;
      });

    const controls = this.graph.controls();
    controls.enableRotate = false;
    controls.minPolarAngle = Math.PI / 2;
    controls.maxPolarAngle = Math.PI / 2;

    fetch('assets/force_graph_data_projs1.json')
      .then((res) => res.json())
      .then((graphData) => {
        console.log(graphData);
        this.fullGraphData = graphData;
        this.applyInitialView();
      });

    // Post processing for glow
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight), // resolution
      1.5, // STRENGTH (higher = more glow)
      1, // RADIUS
      0, // THRESHOLD
    );
    this.graph.postProcessingComposer().addPass(bloomPass);

    setTimeout(() => {
      const scene = this.graph.scene?.();
      if (scene) {
        this.addStarfieldBackground(scene);
      } else {
        console.warn('Scene still undefined');
      }
    }, 500); // Adjust delay if needed
  }

  applyInitialView() {
    // TODO:
    if (this.selected === 'tech_only') {
      const mainConceptNodeIds = new Set(
        this.fullGraphData.nodes.filter((n) => n.type === 'mainConcept').map((n) => n.id),
      );

      const initialLinks = this.fullGraphData.links.filter((l) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
        const targetId = typeof l.target === 'string' ? l.target : l.target.id;
        return sourceId === 'Technology' && mainConceptNodeIds.has(targetId);
      });

      const initialNodeIds = new Set(['Technology', ...Array.from(mainConceptNodeIds)]);
      const initialNodes = this.fullGraphData.nodes.filter((n) => initialNodeIds.has(n.id));

      this.currentGraphData = {
        nodes: initialNodes,
        links: initialLinks,
      };
    } else if (this.selected === 'whole_graph') {
      this.currentGraphData = { ...this.fullGraphData };
    }
    this.graph.graphData(this.currentGraphData);
  }

  closeInfoBox() {
    this.selectedProject = null;
  }

  resetGraph() {
    this.applyInitialView();
    this.graph.centerAt(0, 0, 1000);
    this.graph.zoom(1, 1000);
  }

  centerGraph() {
    if (!this.graph || !this.currentGraphData.nodes.length) return;

    // Use built-in zoomToFit to center and fit the whole graph in view (3d force graph)
    this.graph.zoomToFit(1000, 0.1);
  }

  // Expand a node by adding its children nodes and links if not already visible
  expandNode(nodeId: string) {
    // Find links where source == nodeId and target node is not already visible
    const newLinks = this.fullGraphData.links.filter(
      (l) => l.source === nodeId && !this.currentGraphData.nodes.some((n) => n.id === l.target),
    );

    if (newLinks.length === 0) return; // nothing to expand

    // Find nodes for these links' targets
    const newNodes = newLinks
      .map((l) => this.fullGraphData.nodes.find((n) => n.id === l.target))
      .filter((n): n is (typeof this.fullGraphData.nodes)[0] => !!n);

    // Add new nodes and links to current graph data
    this.currentGraphData.nodes.push(...newNodes);
    this.currentGraphData.links.push(...newLinks);

    // Update graph
    this.graph.graphData(this.currentGraphData);
    this.graph.zoomToFit(1000, 0.1);
  }

  onSelect(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.selected = value;
    this.applyInitialView();
  }

  showMainConceptView(mainConceptId: string) {
    // TODO: add planets around the main concepts
    // Find all subConcept nodes linked from this mainConcept

    const subconceptNodes = this.fullGraphData.nodes.filter((n) => n.type === 'subConcept');

    // Links where source is mainConceptId and target is a subConcept
    const linksForSubconcepts = this.fullGraphData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      const targetId = typeof l.target === 'string' ? l.target : l.target.id;
      return sourceId === mainConceptId && subconceptNodes.some((n) => n.id === targetId);
    });

    // Nodes to show: mainConcept node + its subConcept children
    const nodesToShowIds = new Set<string>();
    nodesToShowIds.add(mainConceptId);
    linksForSubconcepts.forEach((link) => {
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      nodesToShowIds.add(targetId);
    });

    const nodesToShow = this.fullGraphData.nodes.filter((n) => nodesToShowIds.has(n.id));

    // Prevent overlap by spacing subConcepts evenly
    const subConcepts = Array.from(nodesToShowIds).filter((id) => id !== mainConceptId);
    const numSubConcepts = subConcepts.length;
    const minRadius = 10;
    const radius = Math.max(minRadius, numSubConcepts * 80);

    const mainNode = nodesToShow.find((n) => n.id === mainConceptId);
    if (mainNode) {
      mainNode.x = 0;
      mainNode.y = 0;
      mainNode.z = 0;
      mainNode.fx = 0;
      mainNode.fy = 0;
      mainNode.fz = 0;
    }

    subConcepts.forEach((subId, idx) => {
      const angle = (2 * Math.PI * idx) / numSubConcepts;
      const x = (mainNode?.x || 0) + radius * Math.cos(angle);
      const y = (mainNode?.y || 0) + radius * Math.sin(angle);
      const z = 0;

      const subNode = nodesToShow.find((n) => n.id === subId);
      if (subNode) {
        subNode.x = x;
        subNode.y = y;
        subNode.z = z;
        subNode.fx = x;
        subNode.fy = y;
        subNode.fz = z;
      }
    });

    this.currentGraphData = {
      nodes: nodesToShow,
      links: linksForSubconcepts,
    };

    this.graph.graphData(this.currentGraphData);

    // Center and zoom to the mainConcept node
    if (mainNode) {
      this.graph.centerAt(mainNode.x || 0, mainNode.y || 0, 1000); // z distance for zoom
      this.graph.zoom(2, 1000);
    }
  }

  showSubConceptView(subConceptId: string) {
    const maturityStages = ['adopt', 'trial', 'assess', 'identified', 'none'];
    const baseRadius = 50;
    const ringSpacing = 40;
    const maturityToRadius: Record<string, number> = {};
    maturityStages.forEach((stage, i) => (maturityToRadius[stage] = baseRadius + i * ringSpacing));

    const subConceptNode = this.fullGraphData.nodes.find((n) => n.id === subConceptId);
    const links = this.fullGraphData.links.filter((link) => {
      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      return sourceId === subConceptId;
    });

    const relatedProjects = this.fullGraphData.nodes.filter((n) =>
      links.some((link) => link.target === n.id || (link.target as any)?.id === n.id),
    );

    const groupedProjects: Record<string, any[]> = {
      adopt: [],
      trial: [],
      assess: [],
      identified: [],
      none: [],
    };

    relatedProjects.forEach((proj: any) => {
      const stage = (proj.techMaturity || 'none').toLowerCase();
      if (groupedProjects[stage]) {
        groupedProjects[stage].push(proj);
      } else {
        groupedProjects['none'].push(proj);
      }
    });

    maturityStages.forEach((stage) => {
      const radius = maturityToRadius[stage];
      const projects = groupedProjects[stage];
      projects.forEach((project, idx) => {
        const angle = (2 * Math.PI * idx) / projects.length;
        const x = subConceptNode!.x + radius * Math.cos(angle);
        const y = subConceptNode!.y + radius * Math.sin(angle);
        const z = 0;

        // Fix position
        project.x = x;
        project.y = y;
        project.z = z;
        project.fx = x;
        project.fy = y;
        project.fz = z;

        project.parentSubConcept = subConceptNode.id;
        project.targetRadius = radius;
      });
    });

    // Also fix the position of the subConceptNode at center
    subConceptNode!.fx = subConceptNode!.x;
    subConceptNode!.fy = subConceptNode!.y;
    subConceptNode!.fz = subConceptNode!.z;

    this.graph.graphData({
      nodes: [subConceptNode!, ...relatedProjects],
      links: [],
    });

    this.graph.zoomToFit(1000, 0.1);
  }

  addStarfieldBackground(scene: THREE.Scene) {
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 10000;

    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 5000; // make this bigger if needed
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const starsMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);
  }
}
